import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Search, Globe, Share2, BarChart3, Code, FileText, RefreshCw, 
  Loader2, Check, X, ExternalLink, Copy, Download, Settings,
  Facebook, Instagram, Twitter, Youtube, Smartphone, Monitor,
  CheckCircle, XCircle, AlertCircle, Link as LinkIcon
} from 'lucide-react';

export default function SEOTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  const [seoSettings, setSeoSettings] = useState({
    site_title: '',
    site_description: '',
    site_keywords: '',
    og_image: '',
    twitter_handle: '',
    facebook_app_id: '',
    google_analytics_id: '',
    google_search_console_id: '',
    bing_webmaster_id: '',
    facebook_pixel_id: '',
    tiktok_pixel_id: ''
  });
  
  const [sitemap, setSitemap] = useState({ pages: [], sitemap_xml: '', total_pages: 0 });
  const [robotsTxt, setRobotsTxt] = useState('');
  const [integrations, setIntegrations] = useState({});
  const [socialPreview, setSocialPreview] = useState({});
  const [showSitemapDialog, setShowSitemapDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [seoRes, sitemapRes, robotsRes, integrationsRes, socialRes] = await Promise.all([
        api.get('/admin/seo/settings'),
        api.get('/admin/seo/sitemap'),
        api.get('/admin/seo/robots'),
        api.get('/admin/seo/analytics-integration'),
        api.get('/admin/seo/social-preview')
      ]);
      
      setSeoSettings(seoRes.data);
      setSitemap(sitemapRes.data);
      setRobotsTxt(robotsRes.data.content);
      setIntegrations(integrationsRes.data);
      setSocialPreview(socialRes.data);
    } catch (error) {
      console.error('Error loading SEO data:', error);
      toast.error('Failed to load SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSeoSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/seo/settings', seoSettings);
      toast.success('SEO settings saved!');
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSocialPreview = async () => {
    setSaving(true);
    try {
      await api.put('/admin/seo/social-preview', socialPreview);
      toast.success('Social preview settings saved!');
    } catch (error) {
      console.error('Error saving social preview:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copySitemap = () => {
    navigator.clipboard.writeText(sitemap.sitemap_xml);
    toast.success('Sitemap XML copied to clipboard!');
  };

  const downloadSitemap = () => {
    const blob = new Blob([sitemap.sitemap_xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Sitemap downloaded!');
  };

  const copyRobots = () => {
    navigator.clipboard.writeText(robotsTxt);
    toast.success('robots.txt copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="seo-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6 text-primary" />
            SEO & Marketing Tools
          </h2>
          <p className="text-muted-foreground">Optimize your site for search engines and social media</p>
        </div>
        <Button onClick={loadData} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Indexed Pages</p>
              <p className="text-xl font-bold text-blue-600">{sitemap.total_pages}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Integrations Active</p>
              <p className="text-xl font-bold text-green-600">
                {Object.values(integrations).filter(i => i.configured).length}/{Object.keys(integrations).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Globe className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">SEO Score</p>
              <p className="text-xl font-bold text-purple-600">
                {seoSettings.site_title && seoSettings.site_description && seoSettings.site_keywords ? 'Good' : 'Needs Work'}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Share2 className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Social Ready</p>
              <p className="text-xl font-bold text-orange-600">
                {socialPreview.default_share_image ? 'Yes' : 'Setup Needed'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-fit">
          <TabsTrigger value="general" className="gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2">
            <Share2 className="h-4 w-4" />
            Social Media
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="sitemap" className="gap-2">
            <FileText className="h-4 w-4" />
            Sitemap
          </TabsTrigger>
          <TabsTrigger value="structured" className="gap-2">
            <Code className="h-4 w-4" />
            Schema
          </TabsTrigger>
        </TabsList>

        {/* General SEO Tab */}
        <TabsContent value="general" className="mt-6">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              General SEO Settings
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label>Site Title (Max 60 characters)</Label>
                <Input
                  value={seoSettings.site_title}
                  onChange={(e) => setSeoSettings({ ...seoSettings, site_title: e.target.value })}
                  placeholder="Tour Finder PR - Best Tours in Puerto Rico"
                  maxLength={60}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {seoSettings.site_title?.length || 0}/60 characters
                </p>
              </div>
              
              <div>
                <Label>Meta Description (Max 160 characters)</Label>
                <Textarea
                  value={seoSettings.site_description}
                  onChange={(e) => setSeoSettings({ ...seoSettings, site_description: e.target.value })}
                  placeholder="Discover the best tours and adventures in Puerto Rico..."
                  maxLength={160}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {seoSettings.site_description?.length || 0}/160 characters
                </p>
              </div>
              
              <div>
                <Label>Keywords (comma separated)</Label>
                <Input
                  value={seoSettings.site_keywords}
                  onChange={(e) => setSeoSettings({ ...seoSettings, site_keywords: e.target.value })}
                  placeholder="Puerto Rico tours, PR adventures, El Yunque, bioluminescent bay"
                />
              </div>
              
              <div>
                <Label>Default Share Image URL</Label>
                <Input
                  value={seoSettings.og_image}
                  onChange={(e) => setSeoSettings({ ...seoSettings, og_image: e.target.value })}
                  placeholder="https://example.com/share-image.jpg"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Recommended: 1200x630 pixels for optimal social sharing
                </p>
              </div>
              
              <Button onClick={saveSeoSettings} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Save SEO Settings
              </Button>
            </div>
          </Card>
          
          {/* Google Search Preview */}
          <Card className="p-6 mt-4">
            <h3 className="font-semibold text-lg mb-4">Google Search Preview</h3>
            <div className="bg-white border rounded-lg p-4 max-w-xl">
              <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                {seoSettings.site_title || 'Your Site Title'}
              </p>
              <p className="text-green-700 text-sm">
                https://tourfinder.pr
              </p>
              <p className="text-gray-600 text-sm mt-1">
                {seoSettings.site_description || 'Your site description will appear here...'}
              </p>
            </div>
          </Card>
        </TabsContent>

        {/* Social Media Tab */}
        <TabsContent value="social" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Facebook className="h-5 w-5 text-[#1877F2]" />
                Facebook / Meta
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Facebook App ID</Label>
                  <Input
                    value={seoSettings.facebook_app_id}
                    onChange={(e) => setSeoSettings({ ...seoSettings, facebook_app_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                </div>
                <div>
                  <Label>Facebook Pixel ID</Label>
                  <Input
                    value={seoSettings.facebook_pixel_id}
                    onChange={(e) => setSeoSettings({ ...seoSettings, facebook_pixel_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                  <a 
                    href="https://business.facebook.com/events_manager" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    Get your Pixel ID <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Twitter className="h-5 w-5 text-black" />
                Twitter / X
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Twitter Handle</Label>
                  <Input
                    value={seoSettings.twitter_handle}
                    onChange={(e) => setSeoSettings({ ...seoSettings, twitter_handle: e.target.value })}
                    placeholder="@TourFinderPR"
                  />
                </div>
                <div>
                  <Label>Twitter Card Type</Label>
                  <select
                    value={socialPreview.twitter_card || 'summary_large_image'}
                    onChange={(e) => setSocialPreview({ ...socialPreview, twitter_card: e.target.value })}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="summary">Summary</option>
                    <option value="summary_large_image">Summary Large Image</option>
                  </select>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                TikTok
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>TikTok Pixel ID</Label>
                  <Input
                    value={seoSettings.tiktok_pixel_id}
                    onChange={(e) => setSeoSettings({ ...seoSettings, tiktok_pixel_id: e.target.value })}
                    placeholder="ABCDEFGHIJKLMNOP123"
                  />
                  <a 
                    href="https://ads.tiktok.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    Get your TikTok Pixel <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Share2 className="h-5 w-5 text-green-600" />
                Open Graph Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>OG Site Name</Label>
                  <Input
                    value={socialPreview.og_site_name || ''}
                    onChange={(e) => setSocialPreview({ ...socialPreview, og_site_name: e.target.value })}
                    placeholder="Tour Finder PR"
                  />
                </div>
                <div>
                  <Label>Tour Share Template</Label>
                  <Input
                    value={socialPreview.tour_share_template || ''}
                    onChange={(e) => setSocialPreview({ ...socialPreview, tour_share_template: e.target.value })}
                    placeholder="{title} - Book this amazing tour!"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {'{title}'} for tour title
                  </p>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button onClick={saveSeoSettings} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save Social Settings
            </Button>
            <Button onClick={saveSocialPreview} variant="outline" disabled={saving} className="gap-2">
              Save Preview Settings
            </Button>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(integrations).map(([key, integration]) => (
              <Card key={key} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg capitalize">{key.replace(/_/g, ' ')}</h3>
                    <p className="text-sm text-muted-foreground">{integration.instructions}</p>
                  </div>
                  <Badge variant={integration.configured ? 'default' : 'secondary'}>
                    {integration.configured ? (
                      <><CheckCircle className="h-3 w-3 mr-1" /> Connected</>
                    ) : (
                      <><XCircle className="h-3 w-3 mr-1" /> Not Connected</>
                    )}
                  </Badge>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label>Tracking ID / Verification Code</Label>
                    <Input
                      value={seoSettings[`${key}_id`] || ''}
                      onChange={(e) => setSeoSettings({ ...seoSettings, [`${key}_id`]: e.target.value })}
                      placeholder={integration.id || 'Enter your ID...'}
                    />
                  </div>
                  
                  <a 
                    href={integration.setup_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Setup in {key.replace(/_/g, ' ')} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </Card>
            ))}
          </div>
          
          <Button onClick={saveSeoSettings} disabled={saving} className="mt-4 gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save Analytics Settings
          </Button>
        </TabsContent>

        {/* Sitemap Tab */}
        <TabsContent value="sitemap" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Sitemap
              </h3>
              
              <p className="text-muted-foreground mb-4">
                Your sitemap includes <strong>{sitemap.total_pages}</strong> pages.
              </p>
              
              <div className="flex gap-2 mb-4">
                <Button onClick={() => setShowSitemapDialog(true)} variant="outline" className="gap-2">
                  <FileText className="h-4 w-4" />
                  View Sitemap
                </Button>
                <Button onClick={copySitemap} variant="outline" className="gap-2">
                  <Copy className="h-4 w-4" />
                  Copy XML
                </Button>
                <Button onClick={downloadSitemap} className="gap-2">
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              </div>
              
              <div className="bg-muted rounded-lg p-4 text-sm">
                <p className="font-medium mb-2">Submit your sitemap to:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Google Search Console
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Bing Webmaster Tools
                    </a>
                  </li>
                </ul>
              </div>
            </Card>
            
            <Card className="p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Code className="h-5 w-5 text-primary" />
                robots.txt
              </h3>
              
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto mb-4">
                {robotsTxt}
              </pre>
              
              <Button onClick={copyRobots} variant="outline" className="gap-2">
                <Copy className="h-4 w-4" />
                Copy robots.txt
              </Button>
            </Card>
          </div>
          
          {/* Pages List */}
          <Card className="p-6 mt-4">
            <h3 className="font-semibold text-lg mb-4">Indexed Pages ({sitemap.total_pages})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {sitemap.pages.map((page, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                  <div className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{page.url}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      Priority: {page.priority}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {page.changefreq}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Structured Data Tab */}
        <TabsContent value="structured" className="mt-6">
          <Card className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Code className="h-5 w-5 text-primary" />
              Structured Data (JSON-LD)
            </h3>
            
            <p className="text-muted-foreground mb-4">
              Structured data helps search engines understand your content better and can enable rich results.
            </p>
            
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium mb-2">Organization Schema</h4>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`{
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  "name": "${seoSettings.site_title || 'Tour Finder PR'}",
  "description": "${seoSettings.site_description || ''}",
  "url": "https://tourfinder.pr",
  "logo": "${seoSettings.og_image || ''}",
  "sameAs": [
    "https://facebook.com/TourFinderPR",
    "https://instagram.com/TourFinderPR",
    "https://twitter.com/${seoSettings.twitter_handle?.replace('@', '') || ''}"
  ],
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "PR",
    "addressRegion": "Puerto Rico"
  }
}`}
                </pre>
              </div>
              
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium mb-2">Tour Product Schema (Auto-generated per tour)</h4>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
{`{
  "@context": "https://schema.org",
  "@type": "TouristTrip",
  "name": "[Tour Title]",
  "description": "[Tour Description]",
  "image": "[Tour Image]",
  "offers": {
    "@type": "Offer",
    "price": "[Price]",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "touristType": "Adventure travelers",
  "itinerary": {
    "@type": "ItemList",
    "itemListElement": [...]
  }
}`}
                </pre>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">💡 Pro Tips</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Schema markup is automatically added to tour pages</li>
                <li>• Test your structured data at <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer" className="underline">Google's Rich Results Test</a></li>
                <li>• Rich results can increase click-through rates by up to 30%</li>
              </ul>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sitemap Dialog */}
      <Dialog open={showSitemapDialog} onOpenChange={setShowSitemapDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sitemap XML</DialogTitle>
          </DialogHeader>
          <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
            {sitemap.sitemap_xml}
          </pre>
          <DialogFooter>
            <Button onClick={copySitemap} variant="outline" className="gap-2">
              <Copy className="h-4 w-4" />
              Copy
            </Button>
            <Button onClick={downloadSitemap} className="gap-2">
              <Download className="h-4 w-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
